'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import CookieSettingsLink from '@/components/CookieSettingsLink';
import { useLanguage } from '@/context/LanguageContext';

export default function Footer() {
  const { t } = useLanguage();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#050507] border-t border-gold/15 py-16 px-6 relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 bg-purple-brand/10 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-12 pb-12 border-b border-white/[0.08]">
          {/* Brand Column */}
          <div className="space-y-4 max-w-sm">
            <div className="flex items-center gap-3">
              <AppLogo size={36} />
              <span className="text-xl font-black tracking-tight text-white uppercase">
                Onix <span className="text-gold">Digital</span>
              </span>
            </div>
            <p className="text-[13px] text-slate-400 font-medium leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="pt-2">
              <span className="inline-block text-[11px] font-bold text-gold uppercase tracking-widest bg-gold/10 px-3 py-1 rounded-full border border-gold/20">
                onixdigital.pro
              </span>
            </div>
          </div>

          {/* Solutions Column */}
          <div className="space-y-4">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold">
              {t.footer.solutionsTitle}
            </p>
            <div className="space-y-2">
              {t.footer.solutionsList.map((s) => (
                <button
                  key={s}
                  onClick={() => scrollTo('solucions')}
                  className="block text-left text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Navigation Column */}
          <div className="space-y-4">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold">
              {t.footer.navTitle}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => scrollTo('solucions')}
                className="block text-left text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
              >
                {t.header.solutions}
              </button>
              <button
                onClick={() => scrollTo('per-que-web')}
                className="block text-left text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
              >
                {t.header.whyWeb}
              </button>
              <button
                onClick={() => scrollTo('calculadora-roi')}
                className="block text-left text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
              >
                {t.header.roi}
              </button>
              <button
                onClick={() => scrollTo('preus')}
                className="block text-left text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
              >
                {t.header.pricing}
              </button>
              <button
                onClick={() => scrollTo('contacte')}
                className="block text-left text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
              >
                {t.header.contact}
              </button>
            </div>
          </div>

          {/* Contact Column */}
          <div className="space-y-4">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold">
              {t.footer.contactBadge}
            </p>
            <a
              href="https://wa.me/34694919757?text=Hola%2C%20m%27agradaria%20informaci%C3%B3%20sobre%20les%20solucions%20digitals%20d%27Onix%20Digital"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[13px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              WhatsApp: +34 694 91 97 57
            </a>
            <a
              href="tel:+34694919757"
              className="block text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
            >
              {t.footer.phoneLabel} +34 694 91 97 57
            </a>
            <a
              href="mailto:studio@onixdigital.pro"
              className="block text-[13px] font-medium text-slate-300 hover:text-gold transition-colors"
            >
              studio@onixdigital.pro
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8">
          <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
            {t.footer.rights}
          </span>
          <nav className="flex flex-wrap items-center gap-6">
            <Link
              href="/politica-privacitat"
              className="text-[11px] font-medium text-slate-400 hover:text-gold transition-colors"
            >
              {t.footer.legal[0]}
            </Link>
            <Link
              href="/avis-legal"
              className="text-[11px] font-medium text-slate-400 hover:text-gold transition-colors"
            >
              {t.footer.legal[1]}
            </Link>
            <Link
              href="/politica-cookies"
              className="text-[11px] font-medium text-slate-400 hover:text-gold transition-colors"
            >
              {t.footer.legal[2]}
            </Link>
            <div className="text-[11px] font-medium text-slate-400 hover:text-gold transition-colors">
              <CookieSettingsLink />
            </div>
          </nav>
        </div>
      </div>
    </footer>
  );
}
